import Handler from '../Handler';
import LoggerDecorator from '../../../shared/loggers/LoggerDecorator';
import Request from '../Request';
import Response from '../Response';
import { ListableData } from '../../../shared/types/responses';
import {
  GettableData,
} from '../../../shared/types/requests';
import ApiError, {FormError} from '../../../shared/types/Error';
import {Job} from '../../../shared/types/types';
import {backendLog} from '../../../shared/utils/debug';
import {List} from '../../storage/jobs/JobCollectionInterface';
import {getUserId} from '../../factory/firebaseFactory';
import ChecklistCollectionInterface from '../../storage/checklists/ChecklistCollectionInterface';
import TasksCollectionInterface from '../../storage/tasks/TasksCollectionInterface';
import {Checklist, ChecklistInternal, ChecklistType, Section, Task, TaskGroup} from '../../../shared/types/checklist';
import {generateDocumentId} from '../../../shared/utils/generateDocumentId';
import {getApiError, getFormErrors} from '../../../shared/utils/errors';
import {Timestamp} from 'firebase/firestore';
import {collectAllPromises} from '../../../shared/utils/collectPromises';
import {getSmileyApiUrl} from '../../../shared/constants/auth';
import memoryCache, {getCacheKey} from '../../factory/services/getCacher';

export default class ChecklistHandler extends Handler {
  [key: string]: any;
  protected checklistStore: ChecklistCollectionInterface;
  protected taskStore: TasksCollectionInterface;

  public constructor(logger: LoggerDecorator, checklistStore: ChecklistCollectionInterface, taskStore: TasksCollectionInterface) {
    super(logger);
    this.checklistStore = checklistStore;
    this.taskStore = taskStore;
  }

  protected async put(request: Request): Promise<Response<any>> {
    const data = request.data.checklist;
    const tasks: Task[] | undefined = request.data.tasks;
    const job = request.data.job as Job<any>;
    let error: ApiError | undefined;
    let formErrors: FormError[] = [];
    let responseData = {checklist: data, job};

    try {

      if (!data.id) {
        data.id = generateDocumentId();
      }

      const result = await this.checklistStore.put({...data}, {...job});
      responseData = {checklist: result.checklist, job: result.job};

      if (tasks && tasks.length > 500) {
        throw new Error('Unknown server error');
      }

      if (result.job.status !== 'rejected' && typeof tasks !== 'undefined') {
        await collectAllPromises(tasks, (task) => {
          task.checklistId = result.checklist.id;
          task.projectId = result.checklist.projectId;
          return this.taskStore.put(task);
        });
      }

    } catch (err: any) {
      this.logger.error(
          `Failed to save checklist: ${err.stack || err.message}`
      );
      formErrors = getFormErrors(err);
      error = getApiError(err);
    }

    return {
      id: request.id,
      handler: request.handler,
      action: request.action,
      data: {...responseData, formErrors},
      error,
    };
  }

  protected async remove(request: Request): Promise<Response<any>> {
    const data = request.data as {
      checklist: Checklist,
      job: Job<any>
    } ;

    let responseData = {...data};
    let error: ApiError | undefined;

    try {
      await this.taskStore.removeByChecklistId(data.checklist.id as string);
      const result = await this.checklistStore.remove(data.checklist, data.job);
      responseData = {...result};
    } catch (err) {
      error = err as ApiError;
    }

    return {
      id: request.id,
      handler: request.handler,
      action: request.action,
      data: { ...responseData },
      error,
    };
  }

  protected async get(
    request: Request,
  ): Promise<Response<any>> {
    const data = request.data as GettableData;
    let error: ApiError | undefined;
    let checklist: Checklist | undefined;
    try {
      checklist = await this.checklistStore.get(data.id);
    } catch (err) {
      error = err as ApiError;
    }

    return {
      id: request.id,
      handler: request.handler,
      action: request.action,
      data: { checklist: checklist, formErrors: []},
      error,
    };
  }

  protected mapResponseTask2Task(checklistId: string, group: any, task: any): Task {
    return  {
      id: generateDocumentId(),
      ownerId: getUserId(),
      projectId: '',
      checklistId: checklistId,
      name: task.name,
      slug: task.slug,
      groupId: group.id,
      priorityId: task.priorityId || 'normal',
      statusId: 'newnew',
      assignee: {
        id: getUserId(),
        name: '',
      },
      details: {
        order: 0,
        evaluationId: 'unknown',
        timeSpent: 0,
        weight: task.weight || 1,
        taskMetaId: task.id,
      }
    }
  }

  protected async fetch(
      request: Request,
  ): Promise<Response<any>> {
    const data = request.data as GettableData;
    let error: ApiError | undefined;
    let checklist: Checklist | undefined;
    const taskGroups: TaskGroup[] = [];

    try {
      const response = await this.checklistStore.fetch(data.id);
      const checklistId = generateDocumentId();

      if (response.tasks && response.tasks.length > 0) {
        taskGroups.push({
          id: response.id,
          name: response.name,
          slug: response.slug,
          description: response.description,
          tasks: response.tasks.map(t => this.mapResponseTask2Task(checklistId, response, t)),
          subgroups: [],
          count: response.tasks.length,
        });
      }

      if (response.groups) {
        const groups = Object.keys(response.groups).map(key => response.groups[key]);
        for (const group of groups) {
          const tasks: Task[] = group.tasks.map((t): Task => this.mapResponseTask2Task(checklistId, group, t));
          taskGroups.push({
            id: group.id,
            name: group.name,
            slug: group.slug,
            description: group.description,
            tasks: tasks,
            subgroups: [],
            count: group.tasks.length,
          });
        }
      }

      checklist = {
        id: checklistId,
        templateId: response.id,
        type: response.type,
        typeName: response.name,
        projectId: data.projectId,
        ownerId: getUserId(),
        statusId: 'newnew',
        jobIds: [],
        name: 'New checklist',
        url: '',
        createdAt: Timestamp.fromDate(new Date()),
        taskGroups: taskGroups,
      }
    } catch (err) {
      error = err as ApiError;
    }

    return {
      id: request.id,
      handler: request.handler,
      action: request.action,
      data: { checklist: checklist, formErrors: []},
      error,
    };
  }

  protected async sections(
      request: Request,
  ): Promise<Response<any>> {
    let error: ApiError | undefined;
    const sections: Section[] = [];

    try {
      const response = await this.checklistStore.sections();

      for(const sectionId in response) {
        sections.push({...response[sectionId]});
      }

    } catch (err) {
      error = err as ApiError;
    }

    return {
      id: request.id,
      handler: request.handler,
      action: request.action,
      data: { sections: sections, formErrors: []},
      error,
    };
  }

  protected async trashed(
      request: Request,
      statuses: string[] = ['trashed']
  ): Promise<Response<ListableData<Checklist>>> {
    request.data.limit = 50;
    return await this.baseList(request, request.data?.bookmark?.internal || 'utility', statuses);
  }

  protected async list(
      request: Request,
      statuses: string[] = ['inprogress', 'completed', 'restored']
  ): Promise<Response<ListableData<Checklist>>> {
    return await this.baseList(request, request.data?.bookmark?.internal || 'utility', statuses);
  }

  protected async baseList(
    request: Request,
    checklistInternal: ChecklistInternal,
    statuses: string[]
  ): Promise<Response<ListableData<Checklist>>> {
    const data = request.data as {limit?: number, projectId: string, bookmark?: Checklist};
    let error: ApiError | undefined;
    let list: List<Checklist> =  {items: []};

    backendLog(data);
    try {
      list = await this.checklistStore.list({ownerId: getUserId(), projectId: data.projectId, internal: checklistInternal, statuses: statuses}, data.bookmark, data.limit || 25);
    } catch (err) {
      // Need to save in a log db
      backendLog(err);
      error = err as ApiError;
    }

    return {
      id: request.id,
      handler: request.handler,
      action: request.action,
      data: list,
      error,
    };
  }

  protected async getChecklistPost(request: Request): Promise<Response<any>> {
    const data = request.data as {slug: string};
    let error: ApiError | undefined;
    let responseData: any;

    try {
      const endpoint = `${getSmileyApiUrl()}/app/checklists/posts/${data.slug}`;
      const method = 'get';

      const cacheKey = getCacheKey(endpoint, method, data, {});
      responseData = await memoryCache.get(cacheKey);

      if (!responseData) {
        const response = await fetch(endpoint, {
          method,
          credentials: "include"
        });

        responseData = await response.json();

        if (responseData.code > 299) {
          throw responseData;
        }

        await memoryCache.set(cacheKey, responseData);
        backendLog(responseData);
      }
    } catch (err: any) {
      backendLog(err);

      let code = 500;
      if (err.code && err.code < 499) {
        code = err.code;
      }

      if (err.code) {
        error = {
          status: code,
          name: 'Error',
          message: `${err.code}: ${err.message}`,
          error: true,
        };
      }

      if (!err.code) {
        error = err as ApiError;
      }
    }

    return {
      id: request.id,
      handler: request.handler,
      action: request.action,
      data: error ? {} : {...responseData},
      error,
    };
  }
}
